import React, { useState } from 'react';
import { Quote, ChildQuote, QuoteAlliance } from '../../types/quote';
import { useTranslation } from 'react-i18next';
import FormattedCurrency from '../common/FormattedCurrency';
import { Button } from '../common/Button';
import ProfileAvatar from '../common/ProfileAvatar';
import EyeButton from '../common/EyeButton';
import { computeQuoteAmount, computeQuoteTotalAmount } from '../../utils/jobUtils';
import { isQuoteActive } from '../../utils/quoteUtils';
import QuoteConfirmRetireModal from './QuoteConfirmRetiredModal';
import SingleSubmissionModal from './SingleSubmissionModal';
import CollapsibleContent from '../common/CollapsibleContent';

interface MyQuoteCardProps {
  quote: Quote;
  onEdit: (quote: Quote) => void;
  readOnly?: boolean;
}

const MyQuoteCard: React.FC<MyQuoteCardProps> = ({ quote, onEdit, readOnly = false }) => {
  const { t } = useTranslation(['quotes', 'hub', 'common']);

  const [isConfirmRetire, setIsConfirmRetire] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedQuoteToView, setSelectedQuoteToView] = useState<Quote | ChildQuote | null>(null);

  const myQuoteTotal = computeQuoteAmount(quote);
  const grandTotal = computeQuoteTotalAmount(quote);
  const hasAlliances = quote.alliances && quote.alliances.length > 0;
  const isEditable = isQuoteActive(quote);

  const cardTitle =
    quote.status === 'draft'
      ? t('my_submission', { ns: 'quotes', defaultValue: 'My submission' })
      : t('interactionTypes.adjustment_title', { ns: 'hub' });
  const handleViewQuote = (quoteToView: Quote | ChildQuote) => {
    setSelectedQuoteToView(quoteToView);
    setIsViewModalOpen(true);
  };

  const getAllianceOffer = (quoteAlliance: QuoteAlliance) => {
    if (quoteAlliance.child_quote) {
      return computeQuoteAmount(quoteAlliance.child_quote);
    }
    return 0;
  };

  return (
    <div className="relative flex flex-col rounded-[1.5625rem] bg-white p-6 shadow-drop">
      <CollapsibleContent header={<div className=""></div>} expandButtonClassName="self-start">
        <div className="flex flex-col pb-8 md:flex-row">
          <div className="flex max-w-[22rem] flex-col pr-8">
            <h3 className="text-xl font-bold text-secondary">
              <h3 className="text-xl font-bold text-secondary">{cardTitle}</h3>
            </h3>
            <div className="mt-1 font-ambit text-3xl font-bold text-primary">
              <FormattedCurrency value={myQuoteTotal} />
            </div>
            <div className="mt-6 flex items-center gap-8">
              <EyeButton
                onClick={() => handleViewQuote(quote)}
                ariaLabel={t('viewSubmissionButtonLabel', { ns: 'quotes' })}
              />
              {isEditable && !readOnly && (
                <Button
                  variant="primary"
                  className="rounded-full bg-[#007A7A] px-10 py-2.5 font-bold text-white"
                  onClick={() => onEdit(quote)}
                >
                  {t('modify', { ns: 'quotes', defaultValue: 'Modifier' })}
                </Button>
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-1 flex-col md:mt-0 md:pl-10">
            <div className="flex-1">
              <h3 className="mb-6 text-xl font-bold text-secondary">
                {t('team', { ns: 'quotes', defaultValue: 'Équipe' })}
              </h3>

              {hasAlliances ? (
                <div className="grid grid-cols-1 gap-x-12 gap-y-8 md:grid-cols-2">
                  {quote.alliances?.map(quoteAlliance => {
                    const offerValue = getAllianceOffer(quoteAlliance);
                    const freelancer = quoteAlliance.invited_freelancer;

                    return (
                      <div key={quoteAlliance.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <ProfileAvatar
                            profile_id={freelancer.profile_id}
                            first_name={freelancer.first_name}
                            avatar_url={freelancer.avatar_url}
                            className="h-12 w-12 flex-shrink-0"
                          />
                          <div>
                            <p className="text-sm font-bold leading-tight text-secondary">
                              {freelancer.first_name}
                            </p>
                            <p className="text-xs leading-tight text-gray-500">
                              {quoteAlliance.service_category?.label || t('role', { ns: 'quotes' })}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="flex flex-col">
                            <span className="text-sm font-bold tracking-wider text-gray-400">
                              {quoteAlliance.status === 'pending'
                                ? t('createQuoteModal.quoteRequestLabel', { ns: 'quotes' })
                                : t('createQuoteModal.offerLabel', { ns: 'quotes' })}
                            </span>
                            {offerValue === 0 ? (
                              <span className="font-bold italic text-secondary">
                                {t('childQuoteOfferPending', { ns: 'quotes' })}
                              </span>
                            ) : (
                              <FormattedCurrency
                                value={offerValue}
                                className="text-xs font-bold italic text-secondary"
                              />
                            )}
                          </div>
                          {quoteAlliance.child_quote && (
                            <EyeButton
                              onClick={() => handleViewQuote(quoteAlliance.child_quote!)}
                              ariaLabel={t('createQuoteModal.showAllianceButtonLabel', {
                                ns: 'quotes',
                              })}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center py-4 font-medium italic text-gray-400">
                  {t('no_alliance', { ns: 'quotes', defaultValue: 'Aucune alliance' })}
                </div>
              )}
            </div>
          </div>
        </div>
      </CollapsibleContent>

      <div className="mt-auto flex flex-row items-end justify-between">
        <div>
          <h4 className="text-xl font-bold text-secondary">
            {t('grand_total', { ns: 'quotes', defaultValue: 'Grand total' })}
          </h4>
          <FormattedCurrency value={grandTotal} className="text-3xl font-bold text-primary" />
        </div>

        <button
          onClick={() => setIsConfirmRetire(true)}
          className="pb-2 text-sm font-bold text-secondary underline underline-offset-8 transition-colors hover:text-red-600"
        >
          {t('cancel_submission', { ns: 'quotes', defaultValue: 'Annuler mon offre' })}
        </button>
      </div>

      {isConfirmRetire && (
        <QuoteConfirmRetireModal quote={quote} onClose={() => setIsConfirmRetire(false)} />
      )}
      {isViewModalOpen && selectedQuoteToView && (
        <SingleSubmissionModal
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedQuoteToView(null);
          }}
          job={quote.job}
          quote={selectedQuoteToView}
          parentQuote={quote}
        />
      )}
    </div>
  );
};

export default MyQuoteCard;
